/**
 * The share-link wire protocol (PLAN-LAUNCH D1).
 *
 * Shared by the Worker (which writes these responses) and the browser (which
 * reads them), exactly like `sync/protocol.ts`, `ai/protocol.ts` and
 * `history/protocol.ts`. One module so the two halves cannot drift.
 *
 * ```
 * public   GET  /api/shared/:token                     → { ok, recipe, equipment, byline }
 * public   GET  /api/shared-journal/:token             → { ok, journal, batch, byline }
 * public   GET  /api/shared-journal/:token/photos/:id  → one public photo block's bytes
 * public   GET  /api/shared-journal/:token/readings    → the chart's columnar series
 * session  GET  /api/share/recipes/:recipeId           → { ok, links[] }
 * session  POST /api/share/recipes/:recipeId           → { ok, link }
 * session  POST /api/share/batches/:batchId            → { ok, link }
 * session  GET  /api/share/journals/:journalId         → { ok, link | null }
 * session  POST /api/share/journals/:journalId         → { ok, link }
 * session  GET  /api/share/blends/:blendId             → { ok, link | null }
 * session  POST /api/share/blends/:blendId             → { ok, link }
 * session  POST /api/share/links/:token/revoke         → { ok }
 * ```
 *
 * ## Why the public route lives under its own base path
 *
 * `/api/shared` and `/api/share` are two paths on purpose, and the difference is
 * not cosmetic. The Worker's session middleware lets a request through when its
 * path *starts with* one of `PUBLIC_API_PREFIXES` — so the public read has to
 * sit under a prefix that contains nothing else. Mounting the read at
 * `/api/share/:token` and the management routes beside it would mean adding
 * `/api/share/` to that list, which would take create, list and revoke public
 * with it. The separation is what lets the prefix be stated in one line and be
 * obviously safe.
 *
 * ## What the public response carries, and what it must never carry
 *
 * One recipe document and the equipment profile that recipe calculates against
 * — the same pair a {@link BatchDoc} freezes, and the minimum that makes the
 * shared page's OG, volumes and efficiency the numbers the *owner* sees rather
 * than numbers computed against a stranger's default kettle. Nothing else from
 * the workspace crosses the boundary: no other recipe, no batch, no inventory,
 * no settings, no assistant thread, and nothing at all about the owner — not
 * their name, their email or their user id. See `WorkspaceDO.readSharedRecipe`,
 * which is the only method the public path can reach.
 *
 * ## The one thing about the owner that may cross, and only if they said so
 *
 * G2 adds {@link SharedRecipeResponse.byline}: a display name and an avatar URL,
 * and nothing else — no email, no user id, no account state. It is present only
 * when the link was minted with `attributed`, which is a **per-link choice made
 * once, at mint time, defaulting to off** (PLAN-GALLERY G2 [settled]). A link
 * cannot be attributed after the fact, because a URL already in the wild must not
 * change what it says about who wrote the recipe.
 *
 * The avatar URL is `/api/avatars/:userId` (G1), which does name a user id — and
 * that is the one place a user id reaches a public page in this product. It is an
 * *address* for a public image rather than an authorization, and `profile.ts`
 * carries the argument. A link minted anonymously carries no `byline` at all, and
 * neither does an attributed one whose owner has since cleared their profile.
 *
 * ## A journal is a third document a link may name, and it has its own base path
 *
 * PLAN-JOURNAL D-J7. A journal link resolves to a page of blocks rather than to
 * a recipe, so it gets {@link SHARE_JOURNAL_PUBLIC_BASE_PATH} and the page at
 * {@link JOURNAL_PAGE_PREFIX} rather than a branch inside the recipe read: the
 * response is a different shape, and a recipe page that learned to render a
 * journal would be one bug away from rendering half of each. The recipe read
 * answers 404 for a row that names a journal, and this read answers 404 for a
 * row that does not.
 *
 * Two things cross the boundary here that no recipe link has ever needed, and
 * both are routes of their own under the same token rather than fields on the
 * response: a **photograph**'s bytes, served only for a block the owner left
 * public, and the **readings** the chart draws, served only when a public block
 * asks for a chart. `worker/journal-share.ts` argues both.
 */

import type {
  BatchFermentationReading,
  BlendDoc,
  EquipmentProfileDoc,
  JournalBlock,
  RecipeDoc,
} from '@beerwright/commands';
// Relative, not `@/`: this module is pulled into the Worker's compilation by
// `worker/share.ts`, and that tsconfig maps no path alias. `worker/profile.ts`
// reaches the same module the same way.
import { checkInstagram } from '../profile/protocol';

/** Where the owner's link-management routes live. Session-guarded. */
export const SHARE_BASE_PATH = '/api/share';

/**
 * Where the public read lives. **The only path under `/api/` that a request
 * with no session may use to reach workspace data.**
 */
export const SHARE_PUBLIC_BASE_PATH = '/api/shared';

/**
 * Where a shared **journal** is read, and the second path under `/api/` a
 * request with no session may use to reach workspace data (D-J7).
 *
 * A separate word rather than a longer form of `/api/shared`, so that listing
 * it in `PUBLIC_API_PREFIXES` takes nothing else public with it and so that no
 * `startsWith` can reach the owner's routes at `/api/share/*`.
 */
export const SHARE_JOURNAL_PUBLIC_BASE_PATH = '/api/shared-journal';

/** Where the read-only page lives in the SPA. */
export const SHARE_PAGE_PREFIX = '/s/';

/** Where the read-only **journal** page lives in the SPA. */
export const JOURNAL_PAGE_PREFIX = '/j/';

/**
 * How long a token is, in bytes of entropy.
 *
 * 32 bytes — 256 bits, double the 128 the plan asks for — rendered as 43
 * url-safe base64 characters. The token is a bearer credential handed out in a
 * URL that will be pasted into forums and group chats, so the cost of it being
 * generous is a slightly longer link and the cost of it being mean is somebody
 * enumerating other people's recipes.
 */
export const SHARE_TOKEN_BYTES = 32;

/**
 * The longest token string the public route will even look up.
 *
 * A bound rather than an exact length so a future token format is not a
 * migration; anything longer is refused before it reaches D1, and reads as the
 * same 404 as a wrong token.
 */
export const SHARE_TOKEN_MAX_LENGTH = 128;

/**
 * Live links one recipe may have at once.
 *
 * Multiple links per recipe is the feature — one per recipient, so revoking the
 * one you sent to a forum does not break the one you sent to your brew club —
 * and this is the bound that keeps "the feature" from becoming "an unbounded
 * table one POST at a time".
 */
export const MAX_LIVE_LINKS_PER_RECIPE = 20;

/** One live share link, as the owner sees it. */
export interface ShareLink {
  /** The bearer token. Whoever holds it can read the recipe. */
  readonly token: string;
  readonly recipeId: string;
  /** When it was minted, **ms since epoch** (the column is seconds). */
  readonly createdAt: number;
  /**
   * Whether this link's page carries the owner's name.
   *
   * Decided when the link was minted and never afterwards — see the header. The
   * owner's list shows it so that "which of these has my name on it?" is a
   * question the dialog answers rather than one they have to open links to ask.
   */
  readonly attributed: boolean;
  /**
   * The journal this link opens, or `null` for every other kind of link (D-J7).
   *
   * Present so a caller can tell the two apart without inspecting the URL it was
   * handed. A journal's dialog reads it to know it is looking at its own link;
   * the recipe dialog never sees one, because the list it fetches excludes them.
   */
  readonly journalId: string | null;
  /**
   * The blending session this link opens, or `null` for every other kind of
   * link (PLAN-SHARE-BLEND §1).
   *
   * {@link ShareLink.journalId}'s twin, and there for the same reason: a
   * blend's dialog reads it to know it is looking at its own link, and the
   * recipe dialog never sees one because the list it fetches excludes them.
   */
  readonly blendId: string | null;
  /**
   * Whether this is the link a gallery listing points at (PLAN-UX-PASS A2).
   *
   * `gallery_entry.share_token` is a foreign key onto one of these rows, and
   * live-ness is the link's own column — so revoking the link the listing names
   * takes the public card down with it. Publishing does not mint a link of its
   * own: it adopts the *oldest* attributed one the recipe already has (G3), and
   * the oldest is exactly the one a brewer tidying up months later reads as
   * stale.
   *
   * The owner's list carries the fact so the dialog can say which row that is
   * and ask before it goes. Only the recipe list computes it; the Worker's
   * `ShareLinkRow.gallery_listing` explains why `false` is the right answer on
   * every other route.
   */
  readonly galleryListing: boolean;
}

/**
 * Who a shared page says wrote the recipe, when it says anything at all.
 *
 * The whole of what an attributed link publishes about its owner: the name they
 * claimed and the picture they uploaded. Deliberately not a {@link Profile} — the
 * profile type carries `updatedAt`, which is a fact about an account rather than
 * about a recipe page, and a public payload should carry the fields it renders
 * and no others.
 */
export interface ShareByline {
  readonly displayName: string;
  /** `null` when there is no avatar. Renderers draw initials instead. */
  readonly avatarUrl: string | null;
  /** The brewer's short line about themselves, or `null`. */
  readonly bio: string | null;
  /** A bare Instagram handle, or `null`. The page builds the URL; see `instagramUrl`. */
  readonly instagram: string | null;
}

/** `GET /api/share/recipes/:recipeId`. Live links only, newest first. */
export interface ShareLinksResponse {
  readonly ok: true;
  readonly links: readonly ShareLink[];
}

/** `POST /api/share/recipes/:recipeId`. */
export interface ShareLinkResponse {
  readonly ok: true;
  readonly link: ShareLink;
}

/**
 * The body `POST /api/share/recipes/:recipeId` takes. Empty is a valid one.
 *
 * `attributed` is optional and absent means `false`, which is what makes an old
 * bundle — and a `curl` — mint an anonymous link rather than an attributed one by
 * omission. Defaulting the other way would be a payload shape deciding to publish
 * somebody's name.
 */
export interface MintShareLinkRequest {
  readonly attributed?: boolean;
}

/** `GET /api/shared/:token` — the public read. */
export interface SharedRecipeResponse {
  readonly ok: true;
  /**
   * The recipe as it stands right now, not as it stood when the link was made —
   * unless the link names a batch, when it is the recipe **as brewed** (D-TC2)
   * and {@link SharedRecipeResponse.brewedAt} says which day that was.
   */
  readonly recipe: RecipeDoc;
  /** The profile the recipe's volumes and efficiency are computed against. */
  readonly equipment: EquipmentProfileDoc;
  /**
   * The brew day this page is the recipe of, **ms since epoch**, or `null`.
   *
   * Set only for a link that names a batch (D-TC2), which is what a tasting card
   * carries: the page heads itself "As brewed on" that date, because a taster
   * holding a bottle is owed the beer in the bottle rather than this week's
   * edit. `null` for every ordinary share link — there is no brew day behind a
   * recipe — and `null` too for a batch with no recorded brew date, which reads
   * as a page without the line.
   */
  readonly brewedAt: number | null;
  /**
   * Who the page says wrote it, or `null` for the great majority of links.
   *
   * `null` covers three different histories and deliberately renders as one
   * thing: a link minted anonymously, a link minted before G2 existed, and a
   * link whose owner has since deleted their profile or their account. A missing
   * byline is never an error and never a 404 — the recipe is what the recipient
   * followed the link for.
   */
  readonly byline: ShareByline | null;
  /**
   * The gallery listing this page is the front door of, or `null` (G3).
   *
   * Present only while the recipe is published *and* not hidden, and it carries
   * exactly one thing: the entry id, which is what the clone counter is pinged
   * with. Its presence is what puts a "Clone to my workspace" button on the page;
   * an ordinary share link — the great majority — has none, and stays exactly
   * what it always was.
   */
  readonly gallery: { readonly entryId: string } | null;
}

/** `GET /api/share/journals/:journalId`. One link or none — see {@link shareJournalLinksPath}. */
export interface JournalShareLinkResponse {
  readonly ok: true;
  readonly link: ShareLink | null;
}

/** `GET /api/share/blends/:blendId`. One link or none — see {@link shareBlendLinksPath}. */
export interface BlendShareLinkResponse {
  readonly ok: true;
  readonly link: ShareLink | null;
}

/**
 * `GET /api/shared-journal/:token` — the public read of one journal (D-J8).
 *
 * What crosses the boundary, and what never does, is decided in
 * `WorkspaceDO.readSharedJournal` rather than here or in the route. This type
 * is the shape that arrives; that method's TSDoc is the argument for why it is
 * allowed to.
 */
export interface SharedJournalResponse {
  readonly ok: true;
  /**
   * The journal as it stands right now, with **hidden blocks already gone**
   * (D-J6).
   *
   * Live rather than pinned, like a recipe link and for a stronger reason: a
   * journal is a months-long story and the page is meant to grow with it. The
   * blocks are in the document's own array order, which is the brewer's order.
   */
  readonly journal: {
    readonly name: string;
    readonly blocks: readonly JournalBlock[];
    readonly updatedAt: number;
  };
  /**
   * The batches the **public** blocks name, keyed by id (D-M6).
   *
   * A page may be about several — a barrel filled by three brews, a beer
   * brewed three times — so this is a map rather than one document, and a
   * block looks up the id it carries. An id that is absent is a batch that has
   * been deleted, one only a *hidden* block names, or one this build cannot
   * read; all three render as a page without that block rather than as a
   * failure. Nothing else off the `BatchDoc` is here: no stage notes, no
   * tastings, no cellar tasks, no batch photographs.
   */
  readonly batches: Readonly<Record<string, SharedBatch>>;
  /** The blending sessions the **public** blocks name, keyed by id (D-M6). */
  readonly blends: Readonly<Record<string, SharedBlend>>;
  /** Who the page says wrote it, or `null`. {@link SharedRecipeResponse.byline}'s rules exactly. */
  readonly byline: ShareByline | null;
}

/**
 * One batch on a shared journal page, narrowed to what its blocks draw (D-J8).
 *
 * The five fields a `batch-recipe` and a `batch-telemetry` block need, and not
 * one more: the name, the frozen recipe and equipment, the brew day, and the
 * hand readings the chart plots.
 */
export interface SharedBatch {
  readonly name: string;
  readonly recipe: RecipeDoc;
  readonly equipment: EquipmentProfileDoc;
  readonly brewedAt: number | null;
  readonly fermentationReadings: readonly BatchFermentationReading[];
}

/**
 * One blending session, narrowed, on a shared journal page or its own (D-M6).
 *
 * A whole `BlendDoc` in shape, because every figure the block shows is
 * `evaluateBlend` over the document and the block draws the same summary for a
 * stranger as for the owner. Two things are taken out on the way: each
 * component's `batchId` and `blendId`, which are keys into a workspace a reader
 * cannot see, and the session's `cellarTasks`, which are the blender's own
 * cellar queue — a note to themselves about next Tuesday (PLAN-UX-PASS E1). The
 * session's `notes` are taken out on a journal, whose blend block does not draw
 * them, and kept on the blend's own page, which does. All are **blanked rather
 * than deleted** — `notes: ''`, `batchId: null`, `cellarTasks: []` — for the
 * reason the recipe's tags are blanked rather than deleted: what crosses the
 * wire is validated back through `blendDocSchema`, and a document missing a
 * required key would fail that check and take the page with it.
 */
export type SharedBlend = BlendDoc;

/**
 * `GET /api/shared/:token` — the public read of one **blending session**
 * (PLAN-SHARE-BLEND §2).
 *
 * The second shape that route answers, and a client tells the two apart by
 * which document it was handed: this one carries `blend` where
 * {@link SharedRecipeResponse} carries `recipe` and `equipment`. A blend is not
 * a recipe and has neither an equipment profile to calculate against nor a
 * gallery listing it could be the front door of, so those fields are absent
 * rather than null.
 *
 * What crosses the boundary, and what never does, is decided in
 * `WorkspaceDO.readSharedBlend` rather than here or in the route. This type is
 * the shape that arrives; that method's TSDoc is the argument for why it is
 * allowed to.
 *
 * No units ride along, deliberately, and for the reason the shared journal
 * carries none: they are workspace settings rather than facts about the blend,
 * and the page offers the reader the toggle instead.
 */
export interface SharedBlendResponse {
  readonly ok: true;
  /** The session as it stands right now, narrowed. See {@link SharedBlend}. */
  readonly blend: SharedBlend;
  /** Who the page says made it, or `null`. {@link SharedRecipeResponse.byline}'s rules exactly. */
  readonly byline: ShareByline | null;
}

/** A refusal, from either half. `error` is a stable code, never prose. */
export interface ShareErrorResponse {
  readonly ok: false;
  readonly error: string;
}

/** One recipe's links: `GET` lists them, `POST` mints one. */
export function shareLinksPath(recipeId: string): string {
  return `${SHARE_BASE_PATH}/recipes/${encodeURIComponent(recipeId)}`;
}

/**
 * One batch's link: `POST` mints it, or hands back the one it already has.
 *
 * No `GET` beside it, unlike a recipe's: a batch has one live link by
 * construction and the page that mints it is handed that link back, so there is
 * nothing a list would answer that the mint has not already said (D-TC2).
 */
export function shareBatchLinksPath(batchId: string): string {
  return `${SHARE_BASE_PATH}/batches/${encodeURIComponent(batchId)}`;
}

/**
 * One journal's link: `GET` reads it, `POST` mints it or hands back the one it
 * already has (D-J7).
 *
 * A `GET` beside the `POST`, unlike a batch's: the dialog is opened to find out
 * whether this journal is shared at all, and answering that by minting would
 * publish a page nobody asked to publish.
 */
export function shareJournalLinksPath(journalId: string): string {
  return `${SHARE_BASE_PATH}/journals/${encodeURIComponent(journalId)}`;
}

/**
 * One blend's link: `GET` reads it, `POST` mints it or hands back the one it
 * already has (PLAN-SHARE-BLEND §1).
 *
 * A `GET` beside the `POST`, following the journal rather than the batch: the
 * dialog is opened to find out whether this blend is shared at all, and
 * answering that by minting would publish a page nobody asked to publish.
 */
export function shareBlendLinksPath(blendId: string): string {
  return `${SHARE_BASE_PATH}/blends/${encodeURIComponent(blendId)}`;
}

/** Turn one link off. */
export function shareRevokePath(token: string): string {
  return `${SHARE_BASE_PATH}/links/${encodeURIComponent(token)}/revoke`;
}

/** The public read for a token. */
export function sharedRecipePath(token: string): string {
  return `${SHARE_PUBLIC_BASE_PATH}/${encodeURIComponent(token)}`;
}

/** The public read of a journal, for a token. */
export function sharedJournalPath(token: string): string {
  return `${SHARE_JOURNAL_PUBLIC_BASE_PATH}/${encodeURIComponent(token)}`;
}

/**
 * One photograph on a shared journal.
 *
 * The token, not the journal id: a reader has no journal id and is not given
 * one. The route resolves the token to a row, the row to an object, and refuses
 * a photograph whose block the owner hid — so a hidden picture's bytes are a
 * 404 even to somebody who guessed its id.
 */
export function sharedJournalPhotoPath(token: string, photoId: string): string {
  return `${sharedJournalPath(token)}/photos/${encodeURIComponent(photoId)}`;
}

/**
 * One file on a shared journal.
 *
 * {@link sharedJournalPhotoPath} for the other kind of bytes, gated the same
 * way: the route resolves the token to a row and refuses a file whose block the
 * owner hid. It is served as an attachment, so a link to it downloads rather
 * than opens (D-N2), and the page supplies the brewer's own name for it through
 * the anchor's `download` attribute.
 */
export function sharedJournalFilePath(token: string, fileId: string): string {
  return `${sharedJournalPath(token)}/files/${encodeURIComponent(fileId)}`;
}

/** The device series behind a shared journal's chart. Empty unless a public block asks for it. */
export function sharedJournalReadingsPath(token: string, batchId?: string | null): string {
  const base = `${sharedJournalPath(token)}/readings`;
  return batchId === undefined || batchId === null
    ? base
    : `${base}?batch=${encodeURIComponent(batchId)}`;
}

/** The SPA page a token opens. */
export function sharePagePath(token: string): string {
  return `${SHARE_PAGE_PREFIX}${encodeURIComponent(token)}`;
}

/** The SPA page a **journal** token opens. */
export function journalPagePath(token: string): string {
  return `${JOURNAL_PAGE_PREFIX}${encodeURIComponent(token)}`;
}

/** The absolute URL a recipient is given. */
export function shareUrl(token: string, origin: string): string {
  return `${origin.replace(/\/+$/, '')}${sharePagePath(token)}`;
}

/** The absolute URL a journal's recipient is given. */
export function journalShareUrl(token: string, origin: string): string {
  return `${origin.replace(/\/+$/, '')}${journalPagePath(token)}`;
}

/**
 * Read a byline off a public payload without trusting any of it.
 *
 * The same defensive shape `parseProfile` has, and here for a sharper reason: a
 * browser holding a bundle from before G2 has to read a Worker that sends the
 * field, and a browser from after it has to read a Worker that does not. Both
 * answer `null`, which is already the ordinary state of a share page.
 *
 * A byline with no name is not a byline. An empty `displayName` would render as
 * a stray avatar attached to nobody, so it reads as anonymous instead.
 */
/**
 * Read the gallery linkage off a public payload without trusting any of it.
 *
 * Defensive for the same reason {@link parseShareByline} is — an older Worker
 * sends no such field — and strict about the one thing it carries: an entry id
 * that is not a non-empty string is no linkage at all, and no clone button.
 */
export function parseShareGallery(value: unknown): { readonly entryId: string } | null {
  if (typeof value !== 'object' || value === null) return null;
  const { entryId } = value as { entryId?: unknown };
  return typeof entryId === 'string' && entryId.length > 0 ? { entryId } : null;
}

export function parseShareByline(value: unknown): ShareByline | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as {
    displayName?: unknown;
    avatarUrl?: unknown;
    bio?: unknown;
    instagram?: unknown;
  };
  if (typeof record.displayName !== 'string' || record.displayName.length === 0) return null;
  // The handle is re-checked rather than taken on trust: this payload is
  // public, the reader is a browser we do not control, and the value becomes an
  // `href`. A handle that fails the check reads as no handle, the same way a
  // bad avatar reads as initials.
  const instagram = typeof record.instagram === 'string' ? checkInstagram(record.instagram) : null;
  return {
    displayName: record.displayName,
    avatarUrl: typeof record.avatarUrl === 'string' ? record.avatarUrl : null,
    bio: typeof record.bio === 'string' && record.bio.length > 0 ? record.bio : null,
    instagram: instagram !== null && instagram.ok ? instagram.handle : null,
  };
}
